(() => {
  const theme = (window.theme = window.theme || {});
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  theme.formatMoney = (cents) => {
    const format = theme.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const group = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return group(0, ',', '.');
      if (key === 'amount_with_comma_separator') return group(2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return group(0, '.', ',');
      return group(2, ',', '.');
    });
  };

  theme.creativeSlide = (from, to, forward, duration = 1000) => {
    from.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
    to.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
    if (reduceMotion()) return Promise.resolve();

    const timing = { duration, easing: 'ease' };
    const top = forward ? to : from;
    const bottom = forward ? from : to;
    top.style.zIndex = '2';
    bottom.style.zIndex = '1';
    const shade = document.createElement('span');
    shade.className = 'creative-shade';
    bottom.append(shade);

    const animations = forward
      ? [
          to.animate([{ translate: '100% 0' }, { translate: '0 0' }], timing),
          from.animate([{ translate: '0 0' }, { translate: '-20% 0' }], timing),
          shade.animate([{ opacity: 0 }, { opacity: 1 }], timing),
        ]
      : [
          from.animate([{ translate: '0 0' }, { translate: '100% 0' }], timing),
          to.animate([{ translate: '-20% 0' }, { translate: '0 0' }], timing),
          shade.animate([{ opacity: 1 }, { opacity: 0 }], timing),
        ];

    return Promise.all(animations.map((animation) => animation.finished)).finally(() => {
      shade.remove();
      top.style.zIndex = '';
      bottom.style.zIndex = '';
    });
  };

  theme.updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const cartSectionIds = () => {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    const main = document.querySelector('[data-main-cart]');
    if (main) ids.push(main.dataset.mainCart);
    return ids;
  };

  const loadCartRecommendations = async (root) => {
    const recs = root?.querySelector('[data-cart-recs]');
    if (!recs) return;
    const cache = (theme.recsCache = theme.recsCache || {});
    const url = recs.dataset.url;
    try {
      cache[url] = cache[url] || (await fetch(url).then((res) => res.text()));
      const content = parseHTML(cache[url]).querySelector('[data-cart-recs-content]');
      if (!content || !content.querySelector('li')) return;
      recs.querySelector('[data-cart-recs-list]').replaceChildren(content);
      recs.hidden = false;
    } catch (error) {
      /* recommendations unavailable */
    }
  };

  theme.renderCartSections = (sections) => {
    if (!sections) return;
    const drawerHTML = sections['cart-drawer'];
    const current = document.querySelector('#CartDrawer [data-cart-drawer-content]');
    if (drawerHTML && current) {
      const next = parseHTML(drawerHTML).querySelector('[data-cart-drawer-content]');
      if (next) {
        current.replaceWith(next);
        if (document.getElementById('CartDrawer').open) loadCartRecommendations(next);
      }
    }
    const main = document.querySelector('[data-main-cart]');
    if (main && sections[main.dataset.mainCart]) {
      const next = parseHTML(sections[main.dataset.mainCart]).querySelector('[data-main-cart-content]');
      if (next) main.querySelector('[data-main-cart-content]')?.replaceWith(next);
    }
  };

  theme.showCartNotification = (item) => {
    const notification = document.getElementById('CartNotification');
    if (!notification || !item) return false;
    const image = notification.querySelector('[data-notification-image]');
    const src = item.featured_image?.url || item.image;
    image.hidden = !src;
    if (src) image.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
    notification.querySelector('[data-notification-link]').href = item.url;
    notification.querySelector('[data-notification-title]').textContent = item.product_title;
    notification.classList.add('is-open');
    clearTimeout(theme.notificationTimer);
    theme.notificationTimer = setTimeout(() => notification.classList.remove('is-open'), 8000);
    return true;
  };

  theme.addToCart = async (formData) => {
    const ids = cartSectionIds();
    if (ids.length) {
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    const response = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || theme.strings.cartError);
    theme.renderCartSections(data.sections);
    const cart = await fetch(`${theme.routes.cart}.js`).then((res) => res.json());
    theme.updateCartCount(cart.item_count);
    const drawer = document.getElementById('CartDrawer');
    data.notified = false;
    if (theme.settings.cartNotification && !theme.settings.isCartPage && !drawer?.open) {
      data.notified = theme.showCartNotification(data.items ? data.items[0] : data);
    }
    return data;
  };

  theme.changeCartLine = async (line, quantity, row) => {
    row?.classList.add('is-loading');
    try {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || theme.strings.cartUpdateError);
      theme.renderCartSections(data.sections);
      theme.updateCartCount(data.item_count);
    } catch (error) {
      if (!row) return;
      row.classList.remove('is-loading');
      const message = row.querySelector('[data-cart-error]');
      if (message) {
        message.textContent = error.message || theme.strings.cartUpdateError;
        message.hidden = false;
      }
    }
  };

  /* Dialogs */
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.body.classList.add('is-locked');
    dialog.querySelector('[autofocus], input[type="search"]')?.focus();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
      return;
    }
    if (event.target.closest('[data-dialog-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target.matches('dialog[data-light-dismiss]')) event.target.close();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
      event.target._opener?.focus?.();
    },
    true
  );

  /* Cart drawer, cart page and notification */
  const cartDrawer = document.getElementById('CartDrawer');
  const cartLineTimers = new Map();

  const setNoteLabels = (hasNote) => {
    const label = hasNote ? theme.strings.noteEdit : theme.strings.noteAdd;
    document.querySelectorAll('[data-note-open], [data-note-label]').forEach((el) => (el.textContent = label));
  };

  document.addEventListener('click', async (event) => {
    const cartOpener = event.target.closest('[data-cart-open]');
    if (cartOpener && cartDrawer) {
      event.preventDefault();
      openDialog(cartDrawer, cartOpener);
      loadCartRecommendations(cartDrawer);
      return;
    }

    if (event.target.closest('[data-notification-close]')) {
      document.getElementById('CartNotification')?.classList.remove('is-open');
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      theme.changeCartLine(Number(remove.dataset.cartRemove), 0, remove.closest('.cart-item'));
      return;
    }

    const step = event.target.closest('[data-cart-step]');
    if (step) {
      const row = step.closest('.cart-item');
      const input = row.querySelector('[data-cart-qty]');
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.cartStep));
      const line = Number(input.dataset.cartQty);
      clearTimeout(cartLineTimers.get(line));
      cartLineTimers.set(line, setTimeout(() => theme.changeCartLine(line, Number(input.value), row), 350));
      return;
    }

    const quickAdd = event.target.closest('[data-cart-quick-add]');
    if (quickAdd) {
      const data = new FormData();
      data.append('id', quickAdd.dataset.cartQuickAdd);
      data.append('quantity', '1');
      quickAdd.setAttribute('aria-busy', 'true');
      try {
        await theme.addToCart(data);
      } catch (error) {
        /* the product could not be added; the button stays usable */
      } finally {
        quickAdd.removeAttribute('aria-busy');
      }
      return;
    }

    const noteOpen = event.target.closest('[data-note-open]');
    if (noteOpen) {
      const panel = noteOpen.closest('.cart-drawer__footer')?.querySelector('[data-note-panel]');
      panel?.classList.add('is-open');
      panel?.removeAttribute('aria-hidden');
      panel?.querySelector('[data-note-input]')?.focus();
      return;
    }

    const noteClose = event.target.closest('[data-note-close]');
    if (noteClose) {
      const panel = noteClose.closest('[data-note-panel]');
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      return;
    }

    const noteSave = event.target.closest('[data-note-save]');
    if (noteSave) {
      const input = noteSave.parentElement.querySelector('[data-note-input]');
      noteSave.setAttribute('aria-busy', 'true');
      try {
        await fetch(`${theme.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: input.value }),
        });
        setNoteLabels(input.value.trim() !== '');
        const panel = noteSave.closest('[data-note-panel]');
        panel?.classList.remove('is-open');
        panel?.setAttribute('aria-hidden', 'true');
      } finally {
        noteSave.removeAttribute('aria-busy');
      }
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty]');
    if (!input) return;
    const line = Number(input.dataset.cartQty);
    clearTimeout(cartLineTimers.get(line));
    theme.changeCartLine(line, Math.max(0, parseInt(input.value, 10) || 0), input.closest('.cart-item'));
  });

  /* Copy link buttons (blog share) */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-copy-link]');
    if (!button) return;
    try {
      await navigator.clipboard.writeText(button.dataset.copyLink || window.location.href);
      const status = button.closest('[data-copy-scope]')?.querySelector('[data-copy-status]');
      if (status) {
        status.hidden = false;
        setTimeout(() => (status.hidden = true), 2500);
      }
    } catch (error) {
      /* clipboard unavailable */
    }
  });

  /* Sticky header: hide on scroll down, show on scroll up */
  const headerSection = document.querySelector('.section-header');
  const stickyMode = headerSection?.querySelector('[data-sticky]')?.dataset.sticky || 'on_scroll_up';
  if (headerSection && stickyMode === 'on_scroll_up') {
    let lastY = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        headerSection.classList.toggle('is-hidden', y > lastY && y > headerSection.offsetHeight * 2);
        lastY = y;
      },
      { passive: true }
    );
  }

  /* Search shortcut: Ctrl/Cmd + K */
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
  if (isMac) document.querySelectorAll('[data-shortcut-hint]').forEach((el) => (el.textContent = '⌘ K'));
  document.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
    const search = document.getElementById('SearchModal');
    if (!search) return;
    event.preventDefault();
    openDialog(search);
  });

  /* Popups: promo popup, notification banner and bar */
  if (!customElements.get('site-popup')) {
    customElements.define(
      'site-popup',
      class SitePopup extends HTMLElement {
        connectedCallback() {
          this.key = `popup:${this.dataset.key}`;
          this.addEventListener('click', (event) => {
            if (event.target.closest('[data-popup-close]') || event.target === this.querySelector('[data-popup-overlay]')) this.close();
          });
          this.onKeydown = (event) => {
            if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
          };
          document.addEventListener('keydown', this.onKeydown);

          if (window.Shopify?.designMode) {
            const section = this.closest('.shopify-section');
            document.addEventListener('shopify:section:select', (event) => {
              if (event.target === section) this.open();
            });
            document.addEventListener('shopify:section:deselect', (event) => {
              if (event.target === section) this.close(false);
            });
            return;
          }
          if (this.hasAttribute('data-posted')) {
            this.open();
            return;
          }
          if (this.hasAttribute('data-mobile-off') && window.matchMedia('(max-width: 749px)').matches) return;
          if (this.wasDismissed()) return;
          this.schedule();
        }

        disconnectedCallback() {
          document.removeEventListener('keydown', this.onKeydown);
          clearTimeout(this.timer);
        }

        wasDismissed() {
          try {
            if (Number(this.dataset.days) === 0) return sessionStorage.getItem(this.key) === '1';
            return Number(localStorage.getItem(this.key)) > Date.now();
          } catch (error) {
            return false;
          }
        }

        schedule() {
          const ageGate = document.querySelector('age-verification:not([hidden])');
          if (ageGate) {
            this.timer = setTimeout(() => this.schedule(), 1000);
            return;
          }
          this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 0) * 1000);
        }

        open() {
          this.hidden = false;
          requestAnimationFrame(() => this.classList.add('is-open'));
          if (this.hasAttribute('data-lock')) document.body.classList.add('is-locked');
        }

        close(remember = true) {
          this.classList.remove('is-open');
          setTimeout(() => (this.hidden = true), 400);
          if (this.hasAttribute('data-lock') && !document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
          if (!remember) return;
          try {
            const days = Number(this.dataset.days);
            if (days === 0) sessionStorage.setItem(this.key, '1');
            else localStorage.setItem(this.key, String(Date.now() + days * 86400000));
          } catch (error) {
            /* storage unavailable: the popup may show again */
          }
        }
      }
    );
  }

  /* Recently viewed products */
  theme.getRecentlyViewed = () => {
    try {
      const maxAge = (theme.settings?.recentDays || 30) * 86400000;
      return JSON.parse(localStorage.getItem('recently-viewed') || '[]').filter((item) => Date.now() - item.t < maxAge);
    } catch (error) {
      return [];
    }
  };
  const productHandle = document.querySelector('product-info') && window.location.pathname.split('/products/')[1]?.split('/')[0];
  if (productHandle) {
    try {
      const list = theme.getRecentlyViewed().filter((item) => item.h !== productHandle);
      list.unshift({ h: productHandle, t: Date.now() });
      localStorage.setItem('recently-viewed', JSON.stringify(list.slice(0, 20)));
    } catch (error) {
      /* storage unavailable */
    }
  }

  /* Back to top button */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    const toggleBackToTop = () => backToTop.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
    backToTop.hidden = false;
    window.addEventListener('scroll', toggleBackToTop, { passive: true });
    toggleBackToTop();
    backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' }));
  }

  /* Section entrance animations */
  const appearObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        appearObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.01 }
  );
  const observeAppear = (root = document) => root.querySelectorAll('[data-appear]:not(.is-visible)').forEach((el) => appearObserver.observe(el));
  observeAppear();
  document.addEventListener('shopify:section:load', (event) => observeAppear(event.target));
  if (window.Shopify?.designMode) document.querySelectorAll('[data-appear]').forEach((el) => el.classList.add('is-visible'));

  /* Light / dark mode */
  const applyMode = (mode) => {
    const dark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.dataset.scheme = dark ? 'dark' : 'light';
    document.documentElement.dataset.mode = mode;
    document.querySelectorAll('[data-mode-option]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.modeOption === mode ? 'true' : 'false');
    });
  };
  applyMode(document.documentElement.dataset.mode || 'light');
  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-mode-option]');
    if (!option) return;
    const mode = option.dataset.modeOption;
    try {
      localStorage.setItem('theme-mode', mode);
    } catch (error) {
      /* storage unavailable */
    }
    applyMode(mode);
  });
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (document.documentElement.dataset.mode === 'system') applyMode('system');
  });

  /* Countdowns: countdown section, product countdown block */
  if (!customElements.get('count-down')) {
    customElements.define(
      'count-down',
      class CountDown extends HTMLElement {
        connectedCallback() {
          this.end = /^\d+$/.test(this.dataset.end) ? Number(this.dataset.end) * 1000 : new Date(this.dataset.end).getTime();
          this.fields = ['days', 'hours', 'minutes', 'seconds'].map((key) => this.querySelector(`[data-${key}]`));
          if (Number.isNaN(this.end)) return;
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        tick() {
          const left = Math.max(0, this.end - Date.now());
          const values = [
            Math.floor(left / 86400000),
            Math.floor((left / 3600000) % 24),
            Math.floor((left / 60000) % 60),
            Math.floor((left / 1000) % 60),
          ];
          values.forEach((value, i) => {
            if (this.fields[i]) this.fields[i].textContent = String(value).padStart(2, '0');
          });
          if (left > 0) return;
          clearInterval(this.timer);
          if (window.Shopify?.designMode) return;
          if (this.dataset.after === 'hide') {
            (this.closest('[data-countdown-root]') || this.closest('.shopify-section')).hidden = true;
            return;
          }
          const completed = this.querySelector('[data-completed]');
          if (!completed) return;
          this.querySelector('[data-timer]').hidden = true;
          completed.hidden = false;
        }
      }
    );
  }

  /* Marquees: logo strip, image ticker */
  if (!customElements.get('logo-marquee')) {
    customElements.define(
      'logo-marquee',
      class LogoMarquee extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.originals = [...this.track.children];
          this.build();
          this.resizeObserver = new ResizeObserver(() => {
            if (Math.abs(this.clientWidth - this.lastWidth) > 1) this.build();
          });
          this.resizeObserver.observe(this);
        }

        disconnectedCallback() {
          this.resizeObserver?.disconnect();
        }

        build() {
          this.lastWidth = this.clientWidth;
          this.classList.remove('is-running');
          this.track.replaceChildren(...this.originals);
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const setWidth = this.track.scrollWidth + gap;
          if (!setWidth || !this.originals.length) return;

          const copies = Math.max(1, Math.ceil(this.clientWidth / setWidth));
          const half = [];
          for (let i = 0; i < copies; i++) {
            this.originals.forEach((item) => half.push(i === 0 ? item : this.clone(item)));
          }
          const second = half.map((item) => this.clone(item));
          this.track.replaceChildren(...half, ...second);

          const distance = setWidth * copies;
          const itemWidth = setWidth / this.originals.length;
          const pixelsPerSecond = itemWidth / (parseFloat(this.dataset.speed) || 12);
          this.style.setProperty('--duration', `${distance / pixelsPerSecond}s`);
          this.classList.add('is-running');
        }

        clone(item) {
          const copy = item.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          copy.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          return copy;
        }
      }
    );
  }

  /* Card sliders */
  if (!customElements.get('slider-component')) {
    customElements.define(
      'slider-component',
      class SliderComponent extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          if (!this.track) return;
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.dotsWrap = this.querySelector('[data-dots]');
          this.loop = this.hasAttribute('data-loop');
          this.prev?.addEventListener('click', () => this.go(-1));
          this.next?.addEventListener('click', () => this.go(1));
          this.track.addEventListener('scroll', () => this.update(), { passive: true });
          new ResizeObserver(() => this.build()).observe(this.track);
          const delay = Number(this.dataset.autoplay);
          if (delay > 0) {
            this.addEventListener('pointerenter', () => (this.paused = true));
            this.addEventListener('pointerleave', () => (this.paused = false));
            this.addEventListener('focusin', () => (this.paused = true));
            this.addEventListener('focusout', () => (this.paused = false));
            this.timer = setInterval(() => {
              if (!this.paused && !document.hidden) this.go(1, true);
            }, delay);
          }
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        go(direction, wrap = this.loop) {
          const max = this.track.scrollWidth - this.track.clientWidth - 2;
          if (wrap && direction > 0 && this.track.scrollLeft >= max) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else if (wrap && direction < 0 && this.track.scrollLeft <= 2) {
            this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
          } else {
            this.track.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
          }
        }

        step() {
          const [a, b] = this.track.children;
          if (!a) return this.track.clientWidth;
          return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
        }

        pageCount() {
          const first = this.track.children[0];
          if (!first) return 0;
          const perView = Math.max(1, Math.round(this.track.clientWidth / this.step()));
          return Math.max(1, this.track.children.length - perView + 1);
        }

        build() {
          if (this.dotsWrap) {
            const count = this.pageCount();
            this.dotsWrap.replaceChildren();
            if (count > 1) {
              for (let i = 0; i < count; i++) {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'slider__dot';
                dot.setAttribute('aria-label', `${i + 1}`);
                dot.addEventListener('click', () => this.track.scrollTo({ left: i * this.step(), behavior: 'smooth' }));
                this.dotsWrap.append(dot);
              }
            }
          }
          this.update();
        }

        update() {
          const max = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) {
            this.prev.disabled = !this.loop && this.track.scrollLeft <= 2;
            this.prev.hidden = max <= 0;
          }
          if (this.next) {
            this.next.disabled = !this.loop && this.track.scrollLeft >= max;
            this.next.hidden = max <= 0;
          }
          if (this.dotsWrap) {
            const current = Math.round(this.track.scrollLeft / this.step());
            [...this.dotsWrap.children].forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
          }
        }
      }
    );
  }

  /* Predictive search */
  if (!customElements.get('predictive-search')) {
    customElements.define(
      'predictive-search',
      class PredictiveSearch extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input[type="search"]');
          this.results = this.querySelector('[data-search-results]');
          this.defaults = this.querySelector('[data-search-defaults]');
          this.reset = this.querySelector('[data-search-reset]');
          this.input?.addEventListener('input', () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.search(), 250);
          });
          this.reset?.addEventListener('click', () => {
            this.input.value = '';
            this.search();
            this.input.focus();
          });
          this.addEventListener('keydown', (event) => this.onKeydown(event));
        }

        async search() {
          const term = this.input.value.trim();
          this.reset.hidden = !term;
          if (!term) {
            this.results.hidden = true;
            this.results.replaceChildren();
            this.defaults.hidden = false;
            return;
          }
          this.controller?.abort();
          this.controller = new AbortController();
          try {
            const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,article,page&resources[limit]=6&section_id=predictive-search`;
            const response = await fetch(url, { signal: this.controller.signal });
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const content = html.querySelector('[data-predictive-results]');
            this.results.replaceChildren(...(content ? content.childNodes : []));
            this.results.hidden = false;
            this.defaults.hidden = true;
          } catch (error) {
            if (error.name !== 'AbortError') this.results.hidden = true;
          }
        }

        onKeydown(event) {
          if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
          const scope = this.results.hidden ? this.defaults : this.results;
          const links = [...scope.querySelectorAll('a')];
          if (!links.length) return;
          const index = links.indexOf(document.activeElement);
          if (event.key === 'Enter') return;
          event.preventDefault();
          const next = event.key === 'ArrowDown' ? (index + 1) % links.length : (index - 1 + links.length) % links.length;
          links[next].focus();
        }
      }
    );
  }

  /* Quick view */
  const quickView = document.getElementById('QuickView');
  const quickViewBody = quickView?.querySelector('[data-quick-view-body]');

  const initQuickViewProduct = (root) => {
    const form = root.querySelector('[data-quick-form]');
    const variants = JSON.parse(root.querySelector('[data-variants]')?.textContent || '[]');
    const gallery = root.querySelector('[data-qv-gallery]');
    const slides = gallery ? [...gallery.querySelectorAll('[data-qv-slide]')] : [];
    let index = 0;

    const show = (i) => {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      slides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      root.querySelectorAll('[data-qv-thumb]').forEach((thumb, n) => thumb.setAttribute('aria-current', n === index ? 'true' : 'false'));
    };
    root.querySelector('[data-qv-prev]')?.addEventListener('click', () => show(index - 1));
    root.querySelector('[data-qv-next]')?.addEventListener('click', () => show(index + 1));
    root.querySelectorAll('[data-qv-thumb]').forEach((thumb, n) => thumb.addEventListener('click', () => show(n)));

    root.querySelectorAll('[data-qty-step]').forEach((button) =>
      button.addEventListener('click', () => {
        const input = root.querySelector('[name="quantity"]');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.qtyStep));
      })
    );

    root.querySelectorAll('[data-option-index]').forEach((select) =>
      select.addEventListener('change', () => {
        const selected = [...root.querySelectorAll('[data-option-index]')].map((el) =>
          el.matches('select') ? el.value : el.querySelector('input:checked')?.value
        );
        select.querySelector('.variant-picker__value')?.replaceChildren(selected[Number(select.dataset.optionIndex)] || '');
        const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));
        const submit = root.querySelector('[data-quick-submit]');
        const label = submit?.querySelectorAll('.button__label');
        if (!variant) {
          if (submit) submit.disabled = true;
          label?.forEach((el) => (el.textContent = theme.strings.unavailable));
          return;
        }
        form.querySelector('[name="id"]').value = variant.id;
        root.querySelector('[data-qv-price]').textContent = theme.formatMoney(variant.price);
        if (submit) submit.disabled = !variant.available;
        label?.forEach((el) => (el.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut));
        if (variant.featured_media) {
          const target = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.featured_media.id));
          if (target > -1) show(target);
        }
      })
    );

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = form.querySelector('[data-quick-submit]');
      const message = root.querySelector('[data-qv-message]');
      submit.setAttribute('aria-busy', 'true');
      message.hidden = true;
      message.textContent = '';
      try {
        const added = await theme.addToCart(new FormData(form));
        if (added.notified || document.getElementById('CartDrawer')?.open) {
          if (quickView?.open) quickView.close();
          return;
        }
        message.textContent = theme.strings.added;
        message.dataset.state = 'success';
      } catch (error) {
        message.textContent = error.message || theme.strings.cartError;
        message.dataset.state = 'error';
      } finally {
        message.hidden = !message.textContent;
        submit.removeAttribute('aria-busy');
      }
    });
  };

  theme.initProductForm = initQuickViewProduct;
  document.querySelectorAll('[data-product-block]').forEach((root) => initQuickViewProduct(root));
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-block]').forEach((root) => initQuickViewProduct(root));
  });

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-quick-view]');
    if (!opener || !quickView) return;
    event.preventDefault();
    opener.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${opener.dataset.quickView}${opener.dataset.quickView.includes('?') ? '&' : '?'}view=quick-view`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-quick-view-content]');
      if (!content) throw new Error('missing');
      quickViewBody.replaceChildren(content);
      initQuickViewProduct(content);
      openDialog(quickView, opener);
    } catch (error) {
      window.location.href = opener.dataset.quickView;
    } finally {
      opener.removeAttribute('aria-busy');
    }
  });

  quickView?.addEventListener('close', () => {
    setTimeout(() => quickViewBody.replaceChildren(), 300);
  });
})();
